import { InvoiceData } from "../../types/InvoiceData";
import InvoiceUtils from "../../utils/InvoiceUtils";

interface Props {
  data: InvoiceData;
  surface?: "print" | "screen";
}

export const PRINT_PALETTE = {
  text: "#222222",
  strong: "#111111",
  body: "#444444",
  muted: "#666666",
  faint: "#888888",
  label: "#555555",
  wellBg: "#EFEFEF",
  headBg: "#1C1C1C",
  headFg: "#FFFFFF",
  rule: "#EEEEEE",
  ruleStrong: "#CCCCCC",
};

export const SCREEN_PALETTE = {
  text: "var(--text-primary)",
  strong: "var(--text-primary)",
  body: "var(--text-secondary)",
  muted: "var(--text-tertiary)",
  faint: "var(--text-tertiary)",
  label: "var(--text-tertiary)",
  wellBg: "var(--surface-sunken)",
  headBg: "var(--surface-sunken)",
  headFg: "var(--text-secondary)",
  rule: "var(--border-subtle)",
  ruleStrong: "var(--border-default)",
};

export default function InvoicePdfTemplate({ data, surface = "print" }: Props) {
  const c = surface === "screen" ? SCREEN_PALETTE : PRINT_PALETTE;
  const { subtotal, taxRate, taxAmount, total } = InvoiceUtils.calculateTotals(data.items);
  const billToLines = data.billTo.split("\n");

  return (
    <div style={{ fontFamily: "Helvetica, Arial, sans-serif", padding: 48, fontSize: 10, color: c.text }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ fontWeight: "bold", fontSize: 11, marginBottom: 3 }}>{data.from.beneficiaryName}</div>
          <div style={{ lineHeight: 1.5, color: c.body }}>{data.from.addressLine1}</div>
          {data.from.addressLine2 && <div style={{ lineHeight: 1.5, color: c.body }}>{data.from.addressLine2}</div>}
          <div style={{ lineHeight: 1.5, color: c.body }}>{data.from.country}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 34, fontWeight: "bold", color: c.strong }}>INVOICE</div>
          <div style={{ fontSize: 11, color: c.label, marginTop: 4 }}># {data.invoiceNumber}</div>
        </div>
      </div>

      {/* Info row */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 8, color: c.faint, marginBottom: 5 }}>Bill To:</div>
          {billToLines.map((line, i) => (
            <div
              key={i}
              style={{
                fontWeight: i === 0 ? "bold" : "normal",
                fontSize: i === 0 ? 11 : 10,
                marginBottom: 2,
                color: i === 0 ? c.text : c.body,
                lineHeight: 1.5,
              }}
            >
              {line}
            </div>
          ))}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ marginBottom: 10, color: c.muted }}>
            Date: <span>{InvoiceUtils.formatDateForDisplay(data.issueDate)}</span>
          </div>
          <div
            style={{
              backgroundColor: c.wellBg,
              padding: "8px 12px",
              display: "flex",
              justifyContent: "space-between",
              gap: 24,
              minWidth: 230,
            }}
          >
            <span style={{ fontWeight: "bold" }}>Balance Due:</span>
            <span style={{ fontWeight: "bold" }}>{InvoiceUtils.formatCurrency(total, data.currency)}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ marginTop: 8 }}>
        <div style={{ display: "flex", backgroundColor: c.headBg, color: c.headFg, padding: "8px", fontSize: 9, fontWeight: "bold" }}>
          <div style={{ flex: 5 }}>Description</div>
          <div style={{ flex: 2, textAlign: "right" }}>Quantity</div>
          <div style={{ flex: 2, textAlign: "right" }}>Rate</div>
          <div style={{ flex: 2, textAlign: "right" }}>Amount</div>
        </div>
        {data.items.map((item, index) => (
          <div key={index} style={{ display: "flex", padding: "8px", borderBottom: `1px solid ${c.rule}` }}>
            <div style={{ flex: 5 }}>{item.description}</div>
            <div style={{ flex: 2, textAlign: "right" }}>{item.quantity}</div>
            <div style={{ flex: 2, textAlign: "right" }}>{InvoiceUtils.formatCurrency(item.unitPrice, data.currency)}</div>
            <div style={{ flex: 2, textAlign: "right" }}>{InvoiceUtils.formatCurrency(item.quantity * item.unitPrice, data.currency)}</div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <div style={{ width: 210 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: c.muted }}>Subtotal:</span>
            <span>{InvoiceUtils.formatCurrency(subtotal, data.currency)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ color: c.muted }}>Tax ({taxRate}%):</span>
            <span>{InvoiceUtils.formatCurrency(taxAmount, data.currency)}</span>
          </div>
          <div style={{ borderBottom: `1px solid ${c.ruleStrong}`, margin: "6px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontWeight: "bold" }}>Total:</span>
            <span style={{ fontWeight: "bold" }}>{InvoiceUtils.formatCurrency(total, data.currency)}</span>
          </div>
        </div>
      </div>

      {/* Terms */}
      {data.paymentTerms && (
        <div style={{ marginTop: 36 }}>
          <div style={{ fontSize: 8, color: c.faint, marginBottom: 4 }}>Terms:</div>
          <div style={{ color: c.body, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{data.paymentTerms}</div>
        </div>
      )}
    </div>
  );
}
